/**
 * @voltxampere/engine — Unified stamp definition envelope
 *
 * Sprint 5 modelled each component type with a `DCStampFn` in a
 * hardcoded dispatch table, and the planner maintained a separate
 * hardcoded `AUGMENTED_TYPES` set.  Sprint 6 pulled the per-type
 * augmentation metadata into a {@link DCStampDefinition} /
 * {@link TransientStampDefinition} envelope so the planner could
 * consult the registry instead of a second source of truth.
 *
 * Sprint 6.1 re-review flagged the remaining smell: the two
 * envelopes *duplicated* `augments`, and the registry exposed
 * `getDCStampDefinition` vs `getTransientStampDefinition` split for
 * something that was always the same shape.  Sprint 7 collapses
 * both into a single {@link StampDefinition}.
 *
 * Design rules:
 *
 *  - `augments` lives once per component type — the planner reads
 *    it regardless of whether the analysis is DC or transient.
 *  - `applyDC` is mandatory.  Every registered type must at minimum
 *    have a DC stamp (even if it is a no-op, like the DC capacitor
 *    which is an open circuit).
 *  - `applyTransient` is optional.  When absent the transient
 *    registry falls back to `applyDC` — correct for linear time-
 *    invariant components (resistor, sources, ground) and for
 *    nonlinear elements whose companion linearises per Newton
 *    iteration (diode, BJT Ebers-Moll, MOSFET Shichman-Hodges).
 *    Elements whose companion model differs between DC and transient
 *    (capacitor Norton, inductor integration-method-specific RHS)
 *    set `applyTransient` explicitly.
 *
 * This gives the BJT / MOSFET / OpAmp stamps that land in Sprint 7
 * a single line declaration, and keeps the registry to exactly one
 * `Map<type, StampDefinition>` internally.
 *
 * Backwards-compatibility: the legacy names `DCStampDefinition` and
 * `TransientStampDefinition` are kept as aliases of {@link StampDefinition}
 * for one sprint so downstream packages (none yet, but workspace
 * tests exist) compile without edits.  The legacy `defineDCStamp`
 * helper is likewise re-exported as a thin shim.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Convenience constructor — matches the `{ key: value }` call style
 * at the bottom of each stamp module. Accepts either the new
 * `applyDC`/`applyTransient` field names or (via the helper shims
 * below) the legacy `apply` name.
 */
export function defineStamp(def) {
    return def;
}
/**
 * Sprint 6 positional helper, still used by stamp modules that
 * have not yet migrated to the new {@link defineStamp} call
 * signature.  Wraps a DC-only stamp as a full
 * {@link StampDefinition} with no `applyTransient` override — the
 * registry falls back to `applyDC` when a transient lookup hits a
 * definition shaped this way.
 */
export function defineDCStamp(type, apply, augments, junctionTerminals) {
    return junctionTerminals === undefined
        ? { type, augments, applyDC: apply }
        : { type, augments, applyDC: apply, junctionTerminals };
}
//# sourceMappingURL=definition.js.map